import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { action, computed, entries, makeObservable, observable, values } from 'mobx';
import random from 'random/dist/cjs/index.js';
import seed from 'seedrandom';
import { locationNameAlphaSort } from "./sort.js";
// random magic min/max
var minDeviation = 0.000048;
var maxDeviation = 0.000062;
export class Store {
  constructor(filters, adapter, initialResults) {
    _defineProperty(this, "fetching", void 0);
    _defineProperty(this, "forceReload", void 0);
    _defineProperty(this, "resultIds", void 0);
    _defineProperty(this, "needsInitialFetch", void 0);
    _defineProperty(this, "hooks", void 0);
    _defineProperty(this, "sort", void 0);
    _defineProperty(this, "adapter", void 0);
    _defineProperty(this, "filterMap", void 0);
    _defineProperty(this, "collisionMap", void 0);
    _defineProperty(this, "results", void 0);
    makeObservable(this, {
      needsInitialFetch: observable,
      collisionMap: observable.deep,
      results: observable.deep,
      resultIds: observable,
      setResults: action,
      sort: observable.deep,
      hooks: observable.deep,
      updateSort: action,
      startFetch: action,
      replaceAdapter: action,
      endFetch: action,
      fetching: observable,
      forceReload: observable,
      filterMap: observable,
      filteredResults: computed
    });
    this.hooks = {
      afterResultsUpdate: observable.set()
    };
    this.results = observable.map();
    this.collisionMap = observable.map();
    this.sort = {
      comparator: locationNameAlphaSort,
      direction: 'desc',
      when: 'always',
      label: 'Name'
    };
    this.adapter = adapter;
    this.filterMap = {};
    for (var filter of filters) {
      this.filterMap[filter.fieldName] = filter;
    }
    this.resultIds = [];
    this.setResults(initialResults ? initialResults : [], 'replace');
    if (initialResults) this.createSnapshot();
    this.forceReload = false;
    this.needsInitialFetch = !initialResults;
    for (var _filter of filters) {
      this.filterMap[_filter.fieldName].init(this);
    }
  }
  checkCollision(location) {
    var key = "".concat(location.lat, "x").concat(location.lng);
    if (this.collisionMap.has(key)) {
      var _this$collisionMap$ge;
      var collision = (_this$collisionMap$ge = this.collisionMap.get(key)) !== null && _this$collisionMap$ge !== void 0 ? _this$collisionMap$ge : null;
      if (collision === location.id) return location;else {
        var prng = random.clone(seed(location.id));
        location.lat += (prng.boolean() ? -1 : 1) * prng.float(minDeviation, maxDeviation);
        location.lng += (prng.boolean() ? -1 : 1) * prng.float(minDeviation, maxDeviation);
        return location;
      }
    } else {
      this.collisionMap.set(key, location.id);
      return location;
    }
  }
  startFetch() {
    this.fetching = true;
    this.adapter.fetchResults(this);
  }
  endFetch() {
    this.fetching = false;
  }
  updateSort(update) {
    this.sort = _objectSpread(_objectSpread({}, this.sort), update);
  }
  applySnapshot(snapshot) {
    var filterNames = Object.keys(this.filterMap);
    for (var filterName of filterNames) {
      var filter = this.filterMap[filterName];
      filter.applySnapshot(snapshot[filterName]);
      for (var listener of values(filter.applySnapshotListeners)) {
        listener(snapshot[filterName]);
      }
      // filter.afterSetState(this);
    }
  }

  replaceAdapter(adapter) {
    var results = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var operation = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'replace';
    this.adapter = adapter;
    if (results) {
      this.setResults(results, operation);
    } else {
      this.forceReload = true;
    }
  }
  createSnapshot() {
    var deproxyify = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    var snapshot = {};
    for (var [fieldName, filter] of entries(this.filterMap)) {
      var fieldSnapshot = filter.createSnapshot(deproxyify);
      var z = fieldSnapshot;
      for (var listener of values(filter.createSnapshotListeners)) {
        var _listener;
        fieldSnapshot = (_listener = listener(fieldSnapshot)) !== null && _listener !== void 0 ? _listener : fieldSnapshot;
        // allow listener to mutate snapshot? if nothing is returned
        // keep using the same snapshot
      }
      ;
      snapshot[fieldName] = fieldSnapshot;
    }
    return snapshot;
  }
  setResults(results) {
    var operation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'replace';
    if (this.needsInitialFetch) this.needsInitialFetch = false;
    if (operation === 'replace' || this.forceReload) {
      // if this action was invoked as a result of adapter being replaced, we want to ignore
      // the adapter's normal marge behavior but we want to make sure next setResults respects it
      this.forceReload = false;
      this.results.clear();
      this.collisionMap.clear();
      this.resultIds = [];
      for (var result of results) {
        this.results.set(result.id, this.checkCollision(result));
        this.resultIds.push(result.id);
      }
    } else {
      for (var _result of results) {
        if (operation === 'merge' && this.results.has(_result.id)) {
          // operation === merge and it exists already
          var old = this.results.get(_result.id);
          this.results.set(_result.id, this.checkCollision(_objectSpread(_objectSpread({}, old), _result)));
        } else {
          this.results.set(_result.id, this.checkCollision(_result));
        }
      }
      this.resultIds = [...this.results.keys()];
    }
    for (var hook of values(this.hooks.afterResultsUpdate)) {
      hook();
    }
    for (var filter of values(this.filterMap)) {
      filter.needsRefresh = false;
    }
  }
  get needsRefresh() {
    if (this.needsInitialFetch) return true;
    for (var filter of values(this.filterMap)) {
      // console.log(`${filter.fieldName} needsRefresh: ${filter.needsRefresh}`);
      if (filter.needsRefresh) return true;
    }
    return false;
  }
  get filteredResults() {
    var filteredResults = [];
    var results = values(this.results);
    var ids = new Set();
    for (var result of results) {
      var match = true;
      for (var filter of values(this.filterMap)) {
        filter.createSnapshot(); // will invalidate everything
        if (!filter.disabled && !filter.test(result, this)) {
          match = false;
          break;
        }
      }
      if (match) {
        ids.add(result.id);
        filteredResults.push(result);
      }
    }
    // console.log(`${filteredResults.length} results`);
    if (this.sort.when === 'never') return {
      ids,
      results: filteredResults
    };else if (this.sort.when === 'always' || this.sort.when.afterFilter) {
      if (this.sort.direction !== null) {
        var sorted = this.sort.comparator(filteredResults, this);
        if (this.sort.direction === 'desc') return {
          ids,
          results: sorted.reverse()
        };else return {
          ids,
          results: sorted
        };
      }
    }
    return {
      ids,
      results: filteredResults
    };
  }
}
export default Store;
//# sourceMappingURL=Store.js.map